import { getStores } from '@littlebird/sdk';
import {
  observable,
  reaction,
  runInAction,
  type IReactionDisposer,
} from 'mobx';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';
import { logError } from '@/lib/errorLogging';
import { userStore } from '@/stores/user.store';
import { AppCapability, isCapable } from '@/utils';

import { electronApis } from '../electron-api';

import {
  alertUnexpectedRecordingStop,
  rememberRecordingMeeting,
  wasRecordingBeforeReload,
} from './meetings-unexpected-stop';
import { ppDebug } from './meetings.debug-log';
import {
  resolveParkedRoundBeforeResume,
  startLocalPostProcessing,
} from './meetings.post-processing';
import { buildRoundLedgerFields } from './meetings.processing-lifecycle';
import { getMeeting, meetingStore } from './meetings.state';
import {
  TranscriptSyncManager,
  type PendingTranscript,
} from './transcript-sync/TranscriptSyncManager';

import type { InterruptedRecordingRecord } from './meetings.durable-map';
import type { RecordingSession } from './meetings.recording-session';
import type { Meeting } from './meetings.types';
import type { MeetingRecoverablePostProcessingRecordingsPayload } from '@electron/shared/meetings.types';

export const RESUME_GRACE_PERIOD_MS = 15 * 60 * 1000;
const INTERRUPTED_PROMOTION_RETRY_DELAY_MS = 60 * 1000;

async function getRecoveryMeeting(meetingId: string) {
  try {
    await getStores().meetings.ensureLoaded(meetingId);
  } catch (error) {
    logError(error, '[meetings] Failed to load interrupted meeting');
  }
  return getMeeting(meetingId);
}

const SPEAKER_LABEL_PATTERN = /\[[^\]\n]*\]: /g;

function withoutSpeakerLabelsOrWhitespace(text: string) {
  return text.replace(SPEAKER_LABEL_PATTERN, '').replace(/\s+/g, '');
}

// The batch whose response was lost may already be stored, so only other text past the synced offset proves another recording wrote
function hasTextFromAnotherRecording(
  backendTranscript: string,
  pendingTranscript: PendingTranscript
) {
  const textAfterSyncedOffset = withoutSpeakerLabelsOrWhitespace(
    Array.from(backendTranscript)
      .slice(pendingTranscript.currentOffset)
      .join('')
  );
  return (
    textAfterSyncedOffset !== '' &&
    textAfterSyncedOffset !==
      withoutSpeakerLabelsOrWhitespace(pendingTranscript.retryBatch?.text ?? '')
  );
}

export function hasTextAfterSyncedLength(
  record: InterruptedRecordingRecord,
  backendTranscript: string
) {
  if (record.syncedLength === undefined) return false;
  const textAfterSyncedLength = Array.from(backendTranscript)
    .slice(record.syncedLength)
    .join('');
  return withoutSpeakerLabelsOrWhitespace(textAfterSyncedLength) !== '';
}

const RECOVERED_SPEECH_MARKER = {
  speaker: 'Recovered speech',
  text: 'Recorded earlier and added after a connection problem, so it may be out of order.',
};

// Appending at the current end never interleaves with or overwrites another recording's text
function afterTranscriptEnd(
  pendingTranscript: PendingTranscript,
  backendTranscript: string
): PendingTranscript {
  return {
    currentOffset: Array.from(backendTranscript).length,
    deltas: [
      RECOVERED_SPEECH_MARKER,
      ...(pendingTranscript.retryBatch ? [pendingTranscript.retryBatch] : []),
      ...pendingTranscript.deltas,
    ],
  };
}

export async function retryInterruptedTranscript(
  record: InterruptedRecordingRecord,
  session: RecordingSession,
  backendTranscript: { transcript: string; transcriptRevision: string | null }
) {
  if (!record.pendingTranscript) return;
  const transcriptChanged =
    (record.transcriptRevision ?? null) !==
      (backendTranscript.transcriptRevision ?? null) ||
    hasTextFromAnotherRecording(
      backendTranscript.transcript,
      record.pendingTranscript
    );
  if (transcriptChanged) {
    record.pendingTranscript = afterTranscriptEnd(
      record.pendingTranscript,
      backendTranscript.transcript
    );
    record.transcriptRevision = backendTranscript.transcriptRevision ?? null;
    record.replaceWatermark = null;
    ppDebug('replay_moved_to_transcript_end', {
      meetingId: record.meetingId,
      currentOffset: record.pendingTranscript.currentOffset,
    });
  }
  record.recordingSession = session;
  meetingStore.setInterruptedRecording(record);
  const manager = new TranscriptSyncManager({
    meetingId: record.meetingId,
    currentOffset: record.pendingTranscript.currentOffset,
    pendingTranscript: record.pendingTranscript,
    meetingExistsInBackend: true,
    patchTranscript: (_id, offset, delta, speaker, signal) =>
      backendApis.meetingNotes.patchTranscript(
        session,
        offset,
        delta,
        speaker,
        signal
      ),
    onFailure: () => {},
    onProgress: persistTranscript,
  });
  function persistTranscript() {
    record.pendingTranscript = manager.getPendingTranscript();
    if (record.userId === userStore.userId)
      meetingStore.setInterruptedRecording(record);
  }

  try {
    await manager.keepFlushing();
    if (transcriptChanged)
      toast.info('Recovered speech was added to the end of the transcript.');
    return {
      length: manager.getCurrentOffset(),
      movedToEnd: transcriptChanged,
    };
  } finally {
    persistTranscript();
  }
}

// Injected by meetings.query.ts to keep recovery on its transition queue
let enqueuePromoteInterrupted: (meetingId: string) => Promise<void>;

export function setPromoteInterruptedEnqueue(
  enqueue: (meetingId: string) => Promise<void>
) {
  enqueuePromoteInterrupted = enqueue;
}

// Streamed text alone cannot distinguish a finished round from an interrupted one
function hasResolvedMeetingPostProcessing(meeting: Meeting): boolean {
  const hasUnfinalizedLedgerRecord =
    meetingStore.getPostProcessingJob(meeting.id) !== undefined;
  if (hasUnfinalizedLedgerRecord) return false;

  const hasSummary = !!meeting.summary?.trim();
  const hasCompletedSummary =
    meeting.summary_status === 'completed' ||
    meeting.summary_status === 'resummarizing';

  return hasSummary || hasCompletedSummary;
}

/** Only run via the meeting transition queue (single-writer invariant). */
function logUnsyncedSpeechReplay(
  record: InterruptedRecordingRecord,
  outcome: 'replayed' | 'failed',
  pendingDeltaCount: number
) {
  const ageMs = Date.now() - Date.parse(record.interruptedAt);
  electronApis.meetings.logRecordingHealthEvent({
    event_type: 'unsynced_speech_replay',
    meeting_id: record.meetingId.toLowerCase(),
    outcome,
    interruption_reason: record.reason,
    unsynced_age_ms: Number.isFinite(ageMs)
      ? Math.max(0, Math.round(ageMs))
      : 0,
    promotion_attempts: record.promotionAttempts ?? 0,
    pending_delta_count: pendingDeltaCount,
  });
}

export async function performPromoteInterruptedTransition(meetingId: string) {
  if (!isCapable(AppCapability.DesktopMainWindowExclusive)) return;

  const normalizedMeetingId = meetingId.toLowerCase();
  const record = meetingStore.interruptedRecordings.get(normalizedMeetingId);
  if (!record) {
    ppDebug('promotion_skipped', {
      meetingId: normalizedMeetingId,
      reason: 'record_missing',
    });
    return;
  }

  // Queued promotions can straddle an account switch
  if (record.userId !== userStore.userId) {
    ppDebug('promotion_skipped', {
      meetingId: normalizedMeetingId,
      reason: 'other_user',
    });
    return;
  }

  const recording = meetingStore.recording;
  if (
    recording.status !== 'idle' &&
    recording.meetingId?.toLowerCase() === normalizedMeetingId
  ) {
    ppDebug('promotion_skipped', {
      meetingId: normalizedMeetingId,
      reason: 'meeting_live',
    });
    return;
  }

  const meeting = await getRecoveryMeeting(normalizedMeetingId);
  if (record.userId !== userStore.userId) return;
  if (!meeting) {
    // Retry after SDK hydration; a missing local entry does not prove deletion
    ppDebug('promotion_skipped', {
      meetingId: normalizedMeetingId,
      reason: 'store_not_hydrated_retry',
      promotionAttempts: (record.promotionAttempts ?? 0) + 1,
    });
    meetingStore.setInterruptedRecording({
      ...record,
      promotionAttempts: (record.promotionAttempts ?? 0) + 1,
    });
    return;
  }

  if (record.deferSummary) {
    meetingStore.setSummaryFinalizing(normalizedMeetingId, true);
  }
  ppDebug('promotion_started', {
    meetingId: normalizedMeetingId,
    usesLocalPostProcessing: record.usesLocalPostProcessing,
    promotionAttempts: record.promotionAttempts ?? 0,
  });

  const pendingDeltaCount =
    (record.pendingTranscript?.deltas.length ?? 0) +
    (record.pendingTranscript?.retryBatch ? 1 : 0);
  let speechReplayed = false;
  let claimedSession: RecordingSession | undefined;
  try {
    const job = meetingStore.getPostProcessingJob(normalizedMeetingId);
    if (
      record.processingToken &&
      job?.processingToken === record.processingToken &&
      (job.state === 'failed' || !job.releasePending || job.finalized)
    ) {
      record.finalizationStarted = true;
    }
    if (!record.finalizationStarted) {
      const session: RecordingSession = {
        meetingId: normalizedMeetingId,
        deviceId: await electronApis.system.getDeviceId(),
        sessionId: crypto.randomUUID(),
      };
      record.recordingSession = session;
      meetingStore.setInterruptedRecording(record);
      const canonical = await backendApis.meetingNotes.resumeRecording(session);
      claimedSession = session;
      const replay = await retryInterruptedTranscript(
        record,
        session,
        canonical
      );
      if (hasTextAfterSyncedLength(record, canonical.transcript)) {
        record.replaceWatermark = null;
      }
      if (pendingDeltaCount > 0) {
        speechReplayed = true;
        logUnsyncedSpeechReplay(record, 'replayed', pendingDeltaCount);
      }
      if (record.usesLocalPostProcessing) {
        const parkedResolved =
          await resolveParkedRoundBeforeResume(normalizedMeetingId);
        if (!parkedResolved) {
          await startLocalPostProcessing(
            normalizedMeetingId,
            buildRoundLedgerFields({
              meetingId: normalizedMeetingId,
              replaceWatermark: record.replaceWatermark ?? null,
              expectedLength: replay?.length ?? canonical.currentOffset,
              deferSummary: record.deferSummary ?? false,
            }),
            session
          );
        }
      } else {
        await backendApis.meetingNotes.ingestRemainingTranscript(
          normalizedMeetingId
        );
        record.summaryPending = !record.deferSummary;
      }
      record.finalizationStarted = true;
      meetingStore.setInterruptedRecording(record);
    }
    if (record.summaryPending) {
      requestPromotedSummary(normalizedMeetingId, record);
    }
    if (record.recordingSession) {
      await backendApis.meetingNotes.endRecording(record.recordingSession);
    }
    meetingStore.clearInterruptedRecording(normalizedMeetingId);
  } catch (error) {
    logError(error, '[meetings] Failed to promote interrupted meeting');
    if (pendingDeltaCount > 0 && !speechReplayed) {
      logUnsyncedSpeechReplay(record, 'failed', pendingDeltaCount);
    }
    if (claimedSession && !record.finalizationStarted) {
      await backendApis.meetingNotes
        .endRecording(claimedSession)
        .catch((error) =>
          logError(
            error,
            '[meetings] Failed to release interrupted recording session'
          )
        );
    }
    // Recheck the owner before persisting across an account switch
    if (record.userId === userStore.userId) {
      clearTimeout(interruptedPromotionTimers.get(normalizedMeetingId));
      interruptedPromotionTimers.delete(normalizedMeetingId);
      ppDebug('promotion_failed_restored', {
        meetingId: normalizedMeetingId,
        promotionAttempts: (record.promotionAttempts ?? 0) + 1,
      });
      meetingStore.setInterruptedRecording({
        ...record,
        promotionAttempts: (record.promotionAttempts ?? 0) + 1,
      });
      meetingStore.setSummaryFinalizing(normalizedMeetingId, false);
    }
  }
}

/**
 * Queued half of the interrupted-recording recovery scan. The ContextKit fetch
 * already happened outside the queue; this re-validates against current state
 * (a start/stop may have been enqueued while the fetch was in flight — the
 * queue guarantees none is running concurrently with us).
 *
 * Only run via the meeting transition queue (single-writer invariant).
 */
export async function performRecoverInterruptedTransition(
  recordings: MeetingRecoverablePostProcessingRecordingsPayload['recordings']
) {
  const userId = userStore.userId;
  for (const recording of recordings) {
    const meetingId = recording.meetingId.toLowerCase();

    // A meeting that is live or mid-transition right now is neither
    // interrupted nor an orphan, even when a previous round already resolved
    // it (a resumed finalized meeting is both resolved AND live). Releasing
    // its artifacts here would let native archive or delete the directory the
    // live session's lanes are writing into, losing the session's audio and
    // stranding the next round without a manifest.
    const currentRecording = meetingStore.recording;
    if (
      currentRecording.status !== 'idle' &&
      currentRecording.meetingId?.toLowerCase() === meetingId
    ) {
      continue;
    }

    // A durable record proves this is an interrupted session, even when the
    // meeting still has a summary from an earlier finalized round.
    if (meetingStore.interruptedRecordings.has(meetingId)) continue;

    const meeting = await getRecoveryMeeting(meetingId);
    if (userId !== userStore.userId) return;
    if (!meeting) continue;
    // A round this window was recording when it reloaded never finished, even
    // when an earlier round left a summary behind
    const recordingBeforeReload = wasRecordingBeforeReload(meetingId);
    if (!recordingBeforeReload && hasResolvedMeetingPostProcessing(meeting)) {
      // The transcript already reached the backend, so the retained native
      // artifacts are orphans from a completion whose acknowledgement the
      // observer never saw (e.g. it restarted first). Release them.
      void electronApis.meetings
        .acknowledgePostProcessingResult({ meetingId })
        .catch((error) => {
          console.warn(
            '[meetings] Failed to release resolved post-processing artifacts:',
            error
          );
        });
      continue;
    }

    // The ContextKit payload carries no interruption timestamp, so the grace
    // period anchors at detection time.
    meetingStore.setInterruptedRecording({
      meetingId,
      interruptedAt: new Date().toISOString(),
      reason: 'contextkit-crash',
      // Recoverable artifacts only exist because post-processing was on when
      // this meeting recorded; latch true regardless of the flag's value now.
      usesLocalPostProcessing: true,
    });
    if (recordingBeforeReload) {
      rememberRecordingMeeting(null);
      alertUnexpectedRecordingStop({
        meetingId,
        cause: 'interrupted',
        title: 'Recording Stopped',
        message:
          'Recording stopped because Littlebird reloaded during the meeting. Resume to keep recording.',
      });
    }
  }
}

/** One pending timer per interrupted record; exported so the main-window
 * listener teardown in meetings.query.ts can clear any timers still armed. */
export const interruptedPromotionTimers = new Map<
  string,
  ReturnType<typeof setTimeout>
>();

const summaryRequestsInFlight = new Set<string>();

/** Not awaited: a summary request retrying through an outage would hold the
 * transition queue, so a Stop on another meeting would wait behind it. */
function requestPromotedSummary(
  meetingId: string,
  record: InterruptedRecordingRecord
) {
  if (summaryRequestsInFlight.has(meetingId)) return;
  summaryRequestsInFlight.add(meetingId);
  void getStores()
    .meetings.summarizeMeeting(meetingId, { automatic: true })
    .then(() => {
      record.summaryPending = false;
      const stored = meetingStore.interruptedRecordings.get(meetingId);
      if (stored?.summaryPending) {
        meetingStore.setInterruptedRecording({
          ...stored,
          summaryPending: false,
        });
      }
    })
    .catch((error) =>
      logError(error, '[meetings] Failed to summarize promoted meeting')
    )
    .finally(() => summaryRequestsInFlight.delete(meetingId));
}

// Store writes made by a running promotion must not arm another one
const promotionsInFlight = observable.set<string>();

// Kept across listener teardown so re-arming does not push a pending retry back
const promotionRetryDueAt = new Map<string, number>();

/** Arms one promotion timer per interrupted record: the grace remainder for a
 * first attempt, a fixed retry delay after a failed promotion. Reacts to the
 * map so records added later (warm interruptions, recovery scans) and records
 * consumed by resume are picked up without polling. */
export function armInterruptedPromotionTimers(): IReactionDisposer {
  return reaction(
    () =>
      Array.from(meetingStore.interruptedRecordings.values()).filter(
        (record) => !promotionsInFlight.has(record.meetingId)
      ),
    (records) => {
      const liveIds = new Set(records.map((record) => record.meetingId));
      for (const [meetingId, timer] of interruptedPromotionTimers) {
        if (!liveIds.has(meetingId)) {
          clearTimeout(timer);
          interruptedPromotionTimers.delete(meetingId);
        }
      }
      for (const meetingId of promotionRetryDueAt.keys()) {
        if (!liveIds.has(meetingId)) promotionRetryDueAt.delete(meetingId);
      }

      for (const record of records) {
        const replaysNow =
          (record.promotionAttempts ?? 0) === 0 &&
          record.reason === 'transcript-sync-failure';
        const armedTimer = interruptedPromotionTimers.get(record.meetingId);
        if (armedTimer !== undefined) {
          if (!replaysNow) continue;
          clearTimeout(armedTimer);
        }
        const graceRemainder =
          new Date(record.interruptedAt).getTime() +
          RESUME_GRACE_PERIOD_MS -
          Date.now();
        // A record with failed attempts already had its promotion triggered
        // (grace expiry or forfeit), so the remaining grace is irrelevant:
        // retry on the fixed delay, never max(grace, delay), which would make
        // an early-forfeited record wait out its full grace before retrying.
        // Speech that failed to sync at stop is replayed now, so it lands as soon as the backend is reachable
        let delay = replaysNow ? 0 : Math.max(graceRemainder, 0);
        if ((record.promotionAttempts ?? 0) > 0) {
          const retryDueAt =
            promotionRetryDueAt.get(record.meetingId) ??
            Date.now() + INTERRUPTED_PROMOTION_RETRY_DELAY_MS;
          promotionRetryDueAt.set(record.meetingId, retryDueAt);
          delay = Math.max(retryDueAt - Date.now(), 0);
        }
        const timer = setTimeout(() => {
          interruptedPromotionTimers.delete(record.meetingId);
          promotionRetryDueAt.delete(record.meetingId);
          ppDebug('promotion_timer_fired', { meetingId: record.meetingId });
          runInAction(() => promotionsInFlight.add(record.meetingId));
          void enqueuePromoteInterrupted(record.meetingId).finally(() =>
            runInAction(() => promotionsInFlight.delete(record.meetingId))
          );
        }, delay);
        interruptedPromotionTimers.set(record.meetingId, timer);
        ppDebug('promotion_timer_armed', {
          meetingId: record.meetingId,
          delayMs: delay,
          promotionAttempts: record.promotionAttempts ?? 0,
        });
      }
    },
    { fireImmediately: true }
  );
}

const liveUnsyncedMeetingIds = observable.set<string>();

export function setLiveSpeechUnsynced(meetingId: string, unsynced: boolean) {
  runInAction(() => {
    if (unsynced) liveUnsyncedMeetingIds.add(meetingId.toLowerCase());
    else liveUnsyncedMeetingIds.delete(meetingId.toLowerCase());
  });
}

export function getUnsyncedSpeechState(
  meetingId: string
): 'uploading' | 'live' | undefined {
  const record = meetingStore.interruptedRecordings.get(
    meetingId.toLowerCase()
  );
  const pending = record?.pendingTranscript;
  if (pending && (pending.deltas.length > 0 || pending.retryBatch))
    return 'uploading';
  if (
    meetingStore.isMeetingIdRecording(meetingId) &&
    liveUnsyncedMeetingIds.has(meetingId.toLowerCase())
  )
    return 'live';
  return undefined;
}
